import {
  Accordion,
  AccordionItem,
  AccordionPanel,
  AccordionTrigger,
  Box,
  Button,
  Dialog,
  DialogClose,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogPanel,
  DialogPopup,
  DialogTitle,
  Icon,
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
  QuietRow,
  ScrollBand,
  SectionLabel,
  Switch,
  Text,
  Tooltip,
  TooltipPopup,
  TooltipTrigger,
  type Omittable,
} from "@salestrak/ui";
import { useMemo, useState } from "react";

import { EntityGroupTag } from "#app/components/entity";
import { ACCESS_ROW_HEIGHT, AccessRowFrame } from "#app/components/sharing/access-row";
import { HeaderRow } from "#app/components/sharing/share-rows";
import { useMemberEntityResolver } from "#app/lib/data";
import { useWorkspaceData } from "#app/lib/data/workspace-data-provider";
import type { EntityData } from "#app/lib/entity-visuals";
import { useUserSettings } from "#app/lib/hooks/use-user-settings";
import { UNTITLED_FORM, type FormShare } from "#app/lib/forms";
import { getDisplayName } from "#app/lib/utils/get-display-name";

export function FormAccessDialog({
  open,
  onOpenChange,
  name,
  description,
  shares,
  onSharesChange,
  keepReason,
}: Omittable<{
  open: boolean;
  onOpenChange: (open: boolean) => void;
  name: string;
  description: string;
  shares: readonly FormShare[];
  onSharesChange: (shares: FormShare[]) => void;
  /** Set, the viewer may add but not remove: everyone shared at open stays on and locked. */
  keepReason?: string;
}>) {
  const maps = useWorkspaceData();
  const { preferences } = useUserSettings();
  const displayFullNames = preferences?.displayFullNames ?? false;
  const [search, setSearch] = useState("");
  const [openSections, setOpenSections] = useState<string[]>([]);
  // Taken in render so the first frame already locks it.
  const [openedWith, setOpenedWith] = useState<readonly string[] | null>(null);
  if (open && openedWith === null) setOpenedWith(shares.map((share) => share.id));
  if (!open && openedWith !== null) {
    setSearch("");
    setOpenSections([]);
    setOpenedWith(null);
  }

  const label = (entity: EntityData): string =>
    entity.kind === "user"
      ? (getDisplayName(entity.user, displayFullNames) ?? "—")
      : entity.kind === "team"
        ? entity.team.teamName
        : "—";
  // The directory, not the roster: the roster is a partial page and would drop the viewer.
  const memberEntity = useMemberEntityResolver();
  const shared = useMemo(
    () => shares.flatMap((share) => memberEntity(share.id) ?? []),
    [memberEntity, shares],
  );
  const term = search.trim().toLowerCase();
  const results = useMemo<EntityData[]>(() => {
    if (!term) return [];
    const teams = maps.teams
      .filter((team) => team.teamName.toLowerCase().includes(term))
      .map((team): EntityData => ({ kind: "team", id: team.teamId, team }));
    const users = maps.users
      .filter((user) =>
        [user.name, user.username, user.email].some((text) =>
          text?.toLowerCase().includes(term),
        ),
      )
      .map((user): EntityData => ({ kind: "user", id: user.userId, user }));
    return [...teams, ...users];
  }, [maps, term]);

  const isShared = (entity: EntityData) => shares.some((share) => share.id === entity.id);
  const kept = (entity: EntityData) =>
    keepReason != null && isShared(entity) && (openedWith ?? []).includes(entity.id);
  const setShared = (entity: EntityData, on: boolean) =>
    onSharesChange(
      on
        ? [...shares.filter((share) => share.id !== entity.id), { id: entity.id, kind: entity.kind as FormShare["kind"] }]
        : shares.filter((share) => share.id !== entity.id),
    );
  const row = (entity: EntityData, height?: number) => {
    const toggle = (
      <Switch
        aria-label={`${label(entity)} has access`}
        checked={isShared(entity)}
        disabled={kept(entity)}
        onCheckedChange={(on) => setShared(entity, on)}
        size="sm"
      />
    );
    return (
      <AccessRowFrame key={entity.id} entity={entity} label={label(entity)} preview {...(height ? { height } : {})}>
        {keepReason && kept(entity) ? (
          // A disabled Switch takes no hover, so the wrapper opens the tooltip.
          <Tooltip>
            <TooltipTrigger render={<Box cursor="not-allowed" />}>{toggle}</TooltipTrigger>
            <TooltipPopup side="left">{keepReason}</TooltipPopup>
          </Tooltip>
        ) : (
          toggle
        )}
      </AccessRowFrame>
    );
  };
  const sharedOpen = openSections.includes("shared");

  return (
    <Dialog onOpenChange={onOpenChange} open={open}>
      <DialogPopup initialFocus={false}>
        <DialogHeader>
          <DialogTitle render={<Box align="center" direction="row" gap="sm" />}>
            <Icon color="muted" name="clipboard-check" size="md" />
            {name || UNTITLED_FORM}
          </DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <DialogPanel>
          <Box gap="base">
            <InputGroup>
              <InputGroupAddon>
                <Icon color="muted" name="search" size="sm" />
              </InputGroupAddon>
              <InputGroupInput
                onChange={(event) => setSearch(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "Escape" && search) {
                    event.stopPropagation();
                    setSearch("");
                  }
                }}
                placeholder="Add people or teams"
                value={search}
              />
              {search ? (
                <InputGroupAddon align="inline-end">
                  <Button
                    aria-label="Clear search"
                    className="rounded-full"
                    onClick={() => setSearch("")}
                    size="icon-xs"
                    variant="ghost"
                  >
                    <Icon name="x" />
                  </Button>
                </InputGroupAddon>
              ) : null}
            </InputGroup>
            {term ? (
              <Box gap="2xs">
                <Box height={32} justify="center">
                  <SectionLabel>Results</SectionLabel>
                </Box>
                {results.length === 0 ? (
                  <QuietRow height={ACCESS_ROW_HEIGHT}>No members or teams match</QuietRow>
                ) : (
                  <Box>{results.map((entity) => row(entity))}</Box>
                )}
              </Box>
            ) : (
              <Box marginXNegative="lg" overflowX="hidden">
                <Accordion multiple onValueChange={setOpenSections} value={openSections}>
                  <AccordionItem separator={false} value="shared">
                    <HeaderRow>
                      <AccordionTrigger indicator="disclosure" size="sm">
                        <Text size="sm" weight="medium">
                          Shared with
                        </Text>
                      </AccordionTrigger>
                      {!sharedOpen && shared.length > 0 ? (
                        <Box paddingRight="sm" shrink={false}>
                          <EntityGroupTag
                            entities={shared}
                            hoverDisabled
                            interactive={false}
                            max={3}
                            size="xs"
                          />
                        </Box>
                      ) : null}
                    </HeaderRow>
                    <AccordionPanel padding="none">
                      <ScrollBand maxHeight={ACCESS_ROW_HEIGHT * 6 + 8}>
                        {shared.length === 0 ? (
                          <QuietRow height={ACCESS_ROW_HEIGHT}>Nothing shared yet. Add people or teams above.</QuietRow>
                        ) : (
                          shared.map((entity) => row(entity))
                        )}
                      </ScrollBand>
                    </AccordionPanel>
                  </AccordionItem>
                </Accordion>
              </Box>
            )}
          </Box>
        </DialogPanel>
        <DialogFooter variant="bare">
          <DialogClose render={<Button fullWidth variant="outline" />}>Done</DialogClose>
        </DialogFooter>
      </DialogPopup>
    </Dialog>
  );
}
